import type { Tournament } from '../types';

interface SummaryPageProps {
  tournament: Tournament;
}

export default function SummaryPage({ tournament }: SummaryPageProps) {
  const completedMatches = tournament.schedule.flatMap(r => r.matches).filter(m => m.isComplete);
  const totalMatches = tournament.schedule.flatMap(r => r.matches).length;
  const progress = totalMatches > 0 ? (completedMatches.length / totalMatches) * 100 : 0;

  // Calculate final standings
  const standings = tournament.players
    .map(player => {
      let wins = 0;
      let losses = 0;
      let pointsFor = 0;
      let pointsAgainst = 0;

      tournament.schedule.forEach(round => {
        round.matches.forEach(match => {
          if (!match.isComplete) return;

          const isTeamA = match.teamA.player1Id === player.id || match.teamA.player2Id === player.id;
          const isTeamB = match.teamB.player1Id === player.id || match.teamB.player2Id === player.id;

          if (isTeamA) {
            pointsFor += match.teamAScore || 0;
            pointsAgainst += match.teamBScore || 0;
            if ((match.teamAScore || 0) > (match.teamBScore || 0)) {
              wins++;
            } else {
              losses++;
            }
          } else if (isTeamB) {
            pointsFor += match.teamBScore || 0;
            pointsAgainst += match.teamAScore || 0;
            if ((match.teamBScore || 0) > (match.teamAScore || 0)) {
              wins++;
            } else {
              losses++;
            }
          }
        });
      });

      return {
        player,
        wins,
        losses,
        pointsFor,
        pointsAgainst,
        differential: pointsFor - pointsAgainst,
        matches: wins + losses
      };
    })
    .sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (b.differential !== a.differential) return b.differential - a.differential;
      return b.pointsFor - a.pointsFor;
    });

  const getSkillBadgeClass = (skill: number) => {
    switch (skill) {
      case 1: return 'badge-beginner';
      case 2: return 'badge-intermediate';
      case 3: return 'badge-advanced';
      default: return '';
    }
  };

  const getSkillLabel = (skill: number) => {
    switch (skill) {
      case 1: return 'B';
      case 2: return 'I';
      case 3: return 'A';
      default: return '?';
    }
  };

  const getSkillLabelFull = (skill: number) => {
    switch (skill) {
      case 1: return 'Beginner';
      case 2: return 'Intermediate';
      case 3: return 'Advanced';
      default: return 'Unknown';
    }
  };

  return (
    <div>
      <h1>Tournament Summary</h1>

      <div className="card mb-2">
        <h2>{tournament.config.name}</h2>
        <p>
          <strong>Format:</strong> {tournament.config.format === 'rotating' ? 'Rotating Partner Round Robin' :
            tournament.config.format === 'balanced' ? 'Balanced (Strict Skill Matching)' : 
            tournament.config.format === 'swiss' ? 'Swiss Format' : 'Fixed Team Round Robin'}
          <br />
          <strong>Created:</strong> {new Date(tournament.createdAt).toLocaleString()}
          <br />
          <strong>Courts:</strong> {tournament.config.courts} | 
          <strong>Rounds:</strong> {tournament.config.rounds}
        </p>

        <div style={{ marginTop: '1rem' }}>
          <div style={{ 
            background: '#e0e0e0', 
            borderRadius: '4px', 
            height: '30px',
            overflow: 'hidden'
          }}>
            <div style={{
              background: progress === 100 ? '#27ae60' : '#3498db',
              height: '100%',
              width: `${progress}%`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              transition: 'width 0.3s',
              fontSize: '0.875rem'
            }}>
              {progress.toFixed(0)}% Complete
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-2">
        <h2>Final Standings</h2>

        <div className="scroll-hint" style={{ 
          fontSize: '0.75rem', 
          color: '#666', 
          marginBottom: '0.5rem',
          display: 'none'
        }}>
          ← Scroll to see more →
        </div>

        <div className="table-container" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="table standings-table" style={{ minWidth: '650px' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', width: '50px' }}>Rank</th>
                <th>Player</th>
                <th style={{ textAlign: 'center', width: '50px' }}>Skill</th>
                <th style={{ textAlign: 'center', width: '50px' }}>W</th>
                <th style={{ textAlign: 'center', width: '50px' }}>L</th>
                <th style={{ textAlign: 'center', width: '60px' }}>Win%</th>
                <th style={{ textAlign: 'center', width: '60px' }}>PF</th>
                <th style={{ textAlign: 'center', width: '60px' }}>PA</th>
                <th style={{ textAlign: 'center', width: '60px' }}>+/-</th>
                <th style={{ textAlign: 'center', width: '50px' }}>SO</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((s, index) => (
                <tr key={s.player.id}>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {index === 0 && '🥇'}
                    {index === 1 && '🥈'}
                    {index === 2 && '🥉'}
                    {index > 2 && index + 1}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}><strong>{s.player.name}</strong></td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${getSkillBadgeClass(s.player.skill)}`}>
                      {getSkillLabel(s.player.skill)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>{s.wins}</td>
                  <td style={{ textAlign: 'center' }}>{s.losses}</td>
                  <td style={{ textAlign: 'center' }}>{s.matches > 0 ? ((s.wins / s.matches) * 100).toFixed(0) : '0'}%</td>
                  <td style={{ textAlign: 'center' }}>{s.pointsFor}</td>
                  <td style={{ textAlign: 'center' }}>{s.pointsAgainst}</td>
                  <td style={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    color: s.differential > 0 ? '#27ae60' : s.differential < 0 ? '#e74c3c' : '#666'
                  }}>
                    {s.differential > 0 ? '+' : ''}{s.differential}
                  </td>
                  <td style={{ textAlign: 'center' }}>{s.player.timesSatOut}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="summary-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
        <div className="card">
          <h3>Tournament Stats</h3>
          <div className="stats-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
              <span>Total Players</span>
              <strong>{tournament.players.length}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
              <span>Total Matches</span>
              <strong>{totalMatches}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
              <span>Completed Matches</span>
              <strong>{completedMatches.length}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
              <span>Target Score</span>
              <strong>{tournament.config.targetScore}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0' }}>
              <span>Win By</span>
              <strong>{tournament.config.winBy}</strong>
            </div>
          </div>
        </div>

        <div className="card">
          <h3>Player Distribution</h3>
          <div className="distribution-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {['Beginner', 'Intermediate', 'Advanced'].map((label, idx) => {
              const count = tournament.players.filter(p => p.skill === idx + 1).length;
              const percentage = tournament.players.length > 0 ? ((count / tournament.players.length) * 100).toFixed(0) : '0';
              return (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className={`badge ${getSkillBadgeClass(idx + 1)}`}>
                      {getSkillLabel(idx + 1)}
                    </span>
                    {label}
                  </span>
                  <span>
                    <strong>{count}</strong>
                    <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                      ({percentage}%)
                    </span>
                  </span>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0 0.25rem 0', borderTop: '2px solid #ddd', marginTop: '0.25rem' }}>
              <strong>Total</strong>
              <strong>{tournament.players.length}</strong>
            </div>
          </div>
        </div>
      </div>

      {standings.length > 0 && (
        <div className="card champion-card" style={{ marginTop: '1rem' }}>
          <h3>Champion</h3>
          <div className="text-center champion-content" style={{ padding: '1.5rem' }}>
            <div className="trophy-icon" style={{ fontSize: '4rem', lineHeight: 1 }}>🏆</div>
            <h2 className="champion-name" style={{ margin: '0.75rem 0', color: '#f39c12', fontSize: '1.75rem' }}>{standings[0].player.name}</h2>
            <span className={`badge ${getSkillBadgeClass(standings[0].player.skill)}`} style={{ fontSize: '0.875rem' }}>
              {getSkillLabelFull(standings[0].player.skill)}
            </span>
            <div className="champion-stats" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#27ae60' }}>{standings[0].wins}</div>
                <div style={{ fontSize: '0.75rem', color: '#666' }}>Wins</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3498db' }}>{standings[0].pointsFor}</div>
                <div style={{ fontSize: '0.75rem', color: '#666' }}>Points</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e74c3c' }}>+{standings[0].differential}</div>
                <div style={{ fontSize: '0.75rem', color: '#666' }}>Diff</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
